import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import {
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Grid,
  Hidden,
  IconButton,
  Link as MUILink,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from '@material-ui/core';

import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import CloseIcon from '@material-ui/icons/Close';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import LaunchIcon from '@material-ui/icons/Launch';
import MenuIcon from '@material-ui/icons/Menu';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PersonIcon from '@material-ui/icons/Person';

import imgLogo from '../../assets/img/logo.png';

import useStyles from './useStyles';
import useAuth from '../../hooks/useAuth';
import AuthService from '../../services/AuthService';

const createMenu = (label, url) => ({ label, url });

const menus = [
  createMenu('centro de carreiras', '/#centro-de-carreiras'),
  createMenu('cursos gratuitos', '/#cursos-gratuitos'),
  createMenu('benefícios', '/#beneficios'),
  createMenu('oportunidades', '/#oportunidades'),
  createMenu('indique e ganhe', '/#indique-e-ganhe'),
];

export default function Header() {
  const classes = useStyles();

  const { logout, user, context } = useAuth();

  const [expandedMenu, setExpandedMenu] = useState(false);
  const [userPopover, setUserPopover] = useState(null);

  const { st_nomecompleto, st_email } = user;

  const handleMenuToggle = useCallback(() => {
    setExpandedMenu((current) => !current);
  }, []);

  const handlePortalAluno = useCallback(async () => {
    const url = await AuthService.getUrlLoginDiretoPortalAluno(context, user);
    window.open(url, 'uny-portal-aluno');
  }, [context, user]);

  const handleFaleConosco = useCallback(() => {
    // Necessário fechar o popover para que ele
    // não impeça o scroll automático do browser
    setUserPopover(null);
  }, []);

  const handleLogout = useCallback(
    async (e) => {
      e.preventDefault();
      await logout();
    },
    [logout],
  );

  const UserMenu = () => (
    <Popover
      keepMounted
      elevation={5}
      open={Boolean(userPopover)}
      anchorEl={userPopover}
      onClose={() => setUserPopover(null)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <Box px={2} my={2}>
        <Typography variant="subtitle2">Egresso de {context?.st_contexto}</Typography>
      </Box>

      <Box my={1}>
        <Divider />
      </Box>

      <Box px={3} py={2} maxWidth={320}>
        <Grid container spacing={2}>
          <Grid item xs={12} align="center">
            <Typography variant="body2">
              <b>{`${st_nomecompleto}`.toUpperCase()}</b>
            </Typography>
            <Typography variant="caption">{st_email}</Typography>
          </Grid>
          <Grid item xs={12} align="center">
            <Button
              className="no-wrap"
              component={Link}
              to="/perfil"
              variant="outlined"
              size="small"
              color="secondary"
              startIcon={<PersonIcon />}
              onClick={() => setUserPopover(null)}
            >
              Gerenciar seu perfil
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box my={1}>
        <Divider />
      </Box>

      <ListItem button component={HashLink} to="/#fale-conosco" onClick={handleFaleConosco}>
        <ListItemIcon>
          <MailOutlineIcon />
        </ListItemIcon>
        <ListItemText primary="Fale Conosco" primaryTypographyProps={{ variant: 'body2' }} />
      </ListItem>
      <ListItem button onClick={handleLogout}>
        <ListItemIcon>
          <ExitToAppIcon />
        </ListItemIcon>
        <ListItemText primary="Sair" primaryTypographyProps={{ variant: 'body2' }} />
      </ListItem>

      <Box my={1} />
    </Popover>
  );

  return (
    <>
      <Box component="header" className={classes.root} bgcolor="action.hover" py={1}>
        <Container>
          <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap">
            <Grid item className={classes.logo} xs>
              <Link to="/">
                <img className="responsive" src={imgLogo} alt="Unyleya" />
              </Link>
            </Grid>
            <Grid item>
              {/*Botão para exibir/ocultar o menu mobile*/}
              <Hidden mdUp>
                <IconButton onClick={handleMenuToggle}>{expandedMenu ? <CloseIcon /> : <MenuIcon />}</IconButton>
              </Hidden>
              {/*Este é o menu desktop*/}
              <Hidden smDown>
                <nav className={classes.nav}>
                  <ul>
                    {menus.map(({ label, url }) => (
                      <li key={url}>
                        <MUILink component={url.includes('#') ? HashLink : Link} to={url}>
                          {label}
                        </MUILink>
                      </li>
                    ))}
                  </ul>
                </nav>
              </Hidden>
            </Grid>
            {Boolean(context?.st_urlportal) && (
              <>
                <Grid item>
                  <Box height="24px" mx={1}>
                    <Divider orientation="vertical" />
                  </Box>
                </Grid>
                <Grid item>
                  <Button size="small" color="primary" endIcon={<LaunchIcon />} onClick={handlePortalAluno}>
                    <Typography variant="caption">
                      <b>Portal do Aluno</b>
                    </Typography>
                  </Button>
                </Grid>
              </>
            )}
            <Grid item>
              <Box height="24px" mx={1}>
                <Divider orientation="vertical" />
              </Box>
            </Grid>
            <Grid item>
              <Button
                color={userPopover ? 'primary' : 'inherit'}
                startIcon={
                  <Hidden xsDown>
                    <AccountCircleIcon color={userPopover ? 'primary' : 'inherit'} />
                  </Hidden>
                }
                onClick={(e) => setUserPopover(e.currentTarget)}
              >
                {st_nomecompleto.split(' ').shift()}
              </Button>
              <UserMenu />
            </Grid>
          </Grid>
          {/*Este é o menu mobile*/}
          <Hidden mdUp>
            <Collapse in={expandedMenu} timeout="auto" unmountOnExit>
              <List onClick={handleMenuToggle}>
                {menus.map(({ label, url }) => (
                  <ListItem key={url} component="a" button href={url}>
                    <ListItemText primary={label} />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </Hidden>
        </Container>
      </Box>
      <Divider />
    </>
  );
}
